import React, { useState } from 'react';
import { useAppStore } from '@/store';
import { AbsenceType, AbsenceReason } from '@abs/shared-types';
import { todayISO } from '@abs/shared-logic';
import { Button, Select, DatePicker, Input, EmptyState } from '../shared';
import type { SelectOption } from '../shared';

export interface LogAbsencePanelProps {
  courseId: string;
}

const ABSENCE_TYPE_OPTIONS: SelectOption[] = Object.values(AbsenceType)
  .filter((t) => t !== AbsenceType.Present && t !== AbsenceType.Cancelled)
  .map((t) => ({ value: t, label: t.charAt(0).toUpperCase() + t.slice(1).replace(/_/g, ' ') }));

const REASON_OPTIONS: SelectOption[] = [
  { value: '', label: '— Select reason —' },
  ...Object.values(AbsenceReason).map((r) => ({ value: r, label: r.charAt(0).toUpperCase() + r.slice(1).replace(/_/g, ' ') })),
];

export function LogAbsencePanel({ courseId }: LogAbsencePanelProps): React.ReactElement {
  const logAbsence = useAppStore((s) => s.logAbsence);
  const entries = useAppStore((s) => s.entries.filter((e) => e.courseId === courseId));

  const [date, setDate] = useState(todayISO());
  const [type, setType] = useState<AbsenceType>(AbsenceType.Unexcused);
  const [reason, setReason] = useState('');
  const [arrivalTime, setArrivalTime] = useState('');
  const [departureTime, setDepartureTime] = useState('');
  const [note, setNote] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    logAbsence({
      courseId,
      date,
      type,
      reason: (reason || null) as AbsenceReason | null,
      arrivalTime: type === AbsenceType.Late ? (arrivalTime || null) : null,
      departureTime: type === AbsenceType.EarlyDeparture ? (departureTime || null) : null,
      note,
    });
    // Reset form
    setNote('');
    setArrivalTime('');
    setDepartureTime('');
  };

  const recentEntries = [...entries]
    .sort((a, b) => b.date.localeCompare(a.date))
    .slice(0, 10);

  return (
    <div className="log-absence-panel">
      <form className="log-absence-panel__form" onSubmit={handleSubmit}>
        <DatePicker label="Date" value={date} onChange={(e) => setDate(e.target.value)} required />
        <Select
          label="Absence Type"
          options={ABSENCE_TYPE_OPTIONS}
          value={type}
          onChange={(e) => setType(e.target.value as AbsenceType)}
          required
          hint="Unexcused and excused count toward penalties; late and early-departure may convert based on the policy."
        />
        <Select
          label="Reason"
          options={REASON_OPTIONS}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          hint="Optional tag used by trend analytics; does not change the penalty calculation."
        />
        {type === AbsenceType.Late && (
          <Input label="Arrival Time" type="time" value={arrivalTime} onChange={(e) => setArrivalTime(e.target.value)} />
        )}
        {type === AbsenceType.EarlyDeparture && (
          <Input label="Departure Time" type="time" value={departureTime} onChange={(e) => setDepartureTime(e.target.value)} />
        )}
        <div className="input-group">
          <label className="input-group__label" htmlFor="log-note">Note</label>
          <textarea
            id="log-note"
            className="input-group__textarea"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
          />
        </div>
        <Button type="submit" variant="primary">Log Absence</Button>
      </form>

      <div className="log-absence-panel__history">
        <h4>Recent History</h4>
        {recentEntries.length === 0 ? (
          <EmptyState title="No entries yet" description="Log your first absence above." />
        ) : (
          <table className="log-absence-panel__table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>Reason</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {recentEntries.map((entry) => (
                <tr key={entry.id}>
                  <td>{entry.date}</td>
                  <td>{entry.type}</td>
                  <td>{entry.reason ?? '—'}</td>
                  <td>
                    <HistoryEntryActions entryId={entry.id} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function HistoryEntryActions({ entryId }: { entryId: string }): React.ReactElement {
  const deleteEntry = useAppStore((s) => s.deleteEntry);
  return (
    <Button variant="ghost" size="sm" onClick={() => deleteEntry(entryId)}>
      Delete
    </Button>
  );
}
